import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { UnitType } from 'entities/Contract';
// import { ReactComponent as MapIcon } from 'shared/assets/icons/MapIcon.svg';
import BoxInfoImage from 'shared/assets/images/BoxInfoImage.png';
import {
  Box,
  parseDiscountData,
  useGetBoxDiscountedPriceString,
  useGetBoxRentalPriceString,
  useGetBoxRentalPriceStringWithVat,
} from 'entities/Box';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { WarehouseDiscount, WarehouseForBooking } from 'entities/Warehouse';
import { Popup } from 'shared/ui/Popup';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';
import { UserAccountType } from 'entities/User';
import { ReadMore } from 'shared/ui/ReadMore';
import { getSizeCodeTypeString } from 'entities/SizeCode';

interface BoxInfoStackProps {
  boxInfo: Nullable<Box>;
  warehouseInfo: Nullable<WarehouseForBooking>;
  isShowPromotionPriceNeeded: boolean;
  accountType?: Nullable<UserAccountType>;
}

export const BoxInfoStack: FC<BoxInfoStackProps> = memo((props) => {
  const { boxInfo, warehouseInfo, isShowPromotionPriceNeeded, accountType } = props;

  const { t } = useAppTranslation('booking');

  const globalSettings = useAppSelector(getGlobalSettings);

  const showPriceWithVat = globalSettings?.showPriceWithVat;

  const discountedPrice = useGetBoxDiscountedPriceString({
    warehouseInfo,
    boxInfo,
    showPriceWithVat,
    accountType,
    unitType: boxInfo?.unitType,
  });

  const firstMonthDiscount = useMemo((): WarehouseDiscount | undefined => {
    if (!warehouseInfo?.warehouseDiscounts) {
      return;
    }

    const sortedDiscounts = [...warehouseInfo.warehouseDiscounts].sort((a, b) => a.priority - b.priority);

    const actualDiscount = sortedDiscounts?.find((discount) => {
      const includesFirstMonth = discount.discounts.split(',').some((discount) => FIRST_MONTH_DISCOUNT_REGEX.test(discount.trim()));

      const hasUnitSizes = Boolean(discount.unitSizes?.length);

      if (hasUnitSizes && boxInfo) {
        const unitSizes = discount.unitSizes.split(',').map((size) => Number(size));

        const includesSize = unitSizes.includes(boxInfo.sizeCode.square);

        return includesFirstMonth && includesSize;
      }

      return includesFirstMonth && discount.unitType === boxInfo?.unitType;
    });

    return actualDiscount;
  }, [boxInfo, warehouseInfo?.warehouseDiscounts]);

  const parsedDiscount = parseDiscountData(firstMonthDiscount);

  const boxMonthRentalPriceWithVat = useGetBoxRentalPriceStringWithVat(boxInfo, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);

  const boxMonthRentalPrice = useGetBoxRentalPriceString(boxInfo);

  const rentalPrice = showPriceWithVat ? boxMonthRentalPriceWithVat : boxMonthRentalPrice;

  const boxWeekRentalPriceWithVat = useGetBoxRentalPriceStringWithVat(boxInfo, InvoiceFrequencyType.WEEK, warehouseInfo, accountType);

  const boxWeekRentalPrice = useGetBoxRentalPriceString(boxInfo, InvoiceFrequencyType.WEEK);

  const weekRate = showPriceWithVat ? boxWeekRentalPriceWithVat : boxWeekRentalPrice;

  const boxImage = boxInfo?.imageUrl || boxInfo?.sizeCode.imageUrl || BoxInfoImage;

  const size = `${roundNumber(boxInfo?.height)}m (${t('H')}) x ${roundNumber(boxInfo?.width)}m (${t('W')}) x ${roundNumber(
    boxInfo?.length,
  )}m (${t('D')})`;
  const square = boxInfo ? `${boxInfo.sizeCode.square} ${getSizeCodeTypeString(boxInfo.sizeCode.sizeCodeType)}` : undefined;
  const volume = boxInfo ? `${roundNumber(boxInfo.height * boxInfo.width * boxInfo.length)} m³` : undefined;

  const bullets = useMemo(() => {
    const baseBullets: TableRow[] =
      boxInfo?.unitType === UnitType.BOX
        ? [
            { label: t(`${boxInfo?.unitType} number`), value: boxInfo?.name },
            { label: t('Square'), value: square },
            { label: t('Volume'), value: volume },
            { label: t('Box size'), value: size },
            { label: t('Floor'), value: getLocalizedString(boxInfo?.floor?.name) },
            { label: t('Regular price'), value: rentalPrice },
          ]
        : [
            { label: t(`${boxInfo?.unitType} number`), value: boxInfo?.name },
            { label: t('Square'), value: square },
            { label: t('Floor'), value: getLocalizedString(boxInfo?.floor?.name) },
            { label: t('Regular price'), value: rentalPrice },
          ];

    if (weekRate && boxInfo?.unitType === UnitType.BOX) {
      baseBullets.push({ label: t('Regular price'), value: weekRate });
    }

    if (discountedPrice && isShowPromotionPriceNeeded) {
      baseBullets.push({
        label: t('Promotional price'),
        value: (
          <div className="relative">
            {discountedPrice}
            <Popup description={<div className="max-w-[310px]">{parsedDiscount?.description}</div>}>
              <InfoIcon width={17} height={17} className="[&>path]:stroke-primary absolute cursor-pointer right-[-18px] top-0" />
            </Popup>
          </div>
        ),
      });
    }

    return baseBullets;
  }, [
    boxInfo?.floor?.name,
    boxInfo?.name,
    boxInfo?.unitType,
    discountedPrice,
    isShowPromotionPriceNeeded,
    parsedDiscount?.description,
    rentalPrice,
    size,
    square,
    t,
    volume,
    weekRate,
  ]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col items-center">
        <img className="w-full" src={boxImage} alt="Box photo" />
        {/* <Button theme="clear" icon={<MapIcon className="stroke-accent" />} iconPosition="prev">
          {t('view on the warehouse map')}
        </Button> */}
        <ReadMore html={getLocalizedString(boxInfo?.description)} rows={3} />
        {/* <div>{getLocalizedString(boxInfo?.description)}</div> */}
      </div>
      <div className="my-4">
        <BulletsTable rows={bullets} />
      </div>
    </div>
  );
});
